import { Box, Grid, Typography, styled, useTheme } from "@mui/material";

import DatePicker from "components/DatePicker";
import NumberFormats from "components/NumberFormats";
import Select from "components/Select";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import useIsMobileView from "hooks/useIsMobileView";

function ProfitAndLossBox() {
  const theme = useTheme();
  const isMobileView = useIsMobileView();

  const [searchParams, setSearchParams] = useSearchParams();
  const portfolioDate = +searchParams.get("portfolioDate") || 0;
  const startDate = searchParams.get("startDate") || "";

  const portfolioInvestmentList = useSelector(
    (state) => state.portfolio.portfolioInvestmentList?.data
  );

  const portfolioForm = useSelector(
    (state) => state.portfolio.portfolioForm?.data
  );

  const profitState = useMemo(() => {
    const isZero = portfolioInvestmentList?.profitOrLossUSDT === 0;
    const isNegative = portfolioInvestmentList?.profitOrLossUSDT < 0;
    const isPositive = portfolioInvestmentList?.profitOrLossUSDT > 0;

    if (isZero)
      return {
        color: "text.main",
        bg: theme.palette.background.greenGradient,
        sign: "",
      };

    if (isNegative)
      return {
        color: "error.main",
        bg: theme.palette.background.redGradient,
        sign: "",
      };

    if (isPositive)
      return {
        color: "success.main",
        bg: theme.palette.background.greenGradient,
        sign: "+",
      };
  }, [
    portfolioInvestmentList,
    theme.palette.background.greenGradient,
    theme.palette.background.redGradient,
  ]);

  const handleChangeStartDate = (e) => {
    const allSearchParams = Object.fromEntries([...searchParams]);

    setSearchParams({
      ...allSearchParams,
      startDate: e,
    });
  };

  const handleChangeDate = (e) => {
    const value = e.target.value;

    const allSearchParams = Object.fromEntries([...searchParams]);

    const newSearchParams = {
      ...allSearchParams,
      portfolioDate: value,
    };

    delete newSearchParams?.startDate;

    setSearchParams(newSearchParams);
  };

  return (
    <InvestmentBox color={profitState?.bg} mb={1}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Grid container mt={2} alignItems="center">
          <Grid item md={8} sm={6} xs={12}>
            <Typography variant="h5" color="text.main" mb={2}>
              Profit & Loss
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            display="flex"
            alignItems="center"
            justifyContent={isMobileView ? "flex-start" : "flex-end"}
          >
            <Box display="flex" alignItems="center">
              <Typography variant="subtitle1" mr={1} color="text.main">
                Date:
              </Typography>

              <SmallSelect
                items={portfolioForm?.lisDateType}
                itemValue="id"
                itemText="name"
                value={portfolioDate}
                onChange={handleChangeDate}
              />
              <BoxDatePicker>
                {portfolioDate === 5 && (
                  <DatePicker
                    value={startDate}
                    onChange={handleChangeStartDate}
                  />
                )}
              </BoxDatePicker>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Typography variant="h6" color={profitState?.color} mb={1} mt={1}>
        {profitState?.sign}
        <NumberFormats
          value={portfolioInvestmentList?.profitOrLossUSDT || "-"}
        />{" "}
        <Typography
          component="small"
          color="text.secondary"
          variant="subtitle2"
        >
          USDT
        </Typography>
      </Typography>

      <Typography variant="h6" color={profitState?.color} mb={1}>
        {profitState?.sign}
        <NumberFormats
          value={portfolioInvestmentList?.profitOrLossToman || "-"}
        />{" "}
        <Typography
          component="small"
          color="text.secondary"
          variant="subtitle2"
        >
          TOMAN
        </Typography>
      </Typography>

      {portfolioInvestmentList?.dateStart && (
        <Typography variant="h6" fontWeight={500} color="text.main">
          From:{" "}
          <Typography
            component="small"
            color="text.secondary"
            variant="subtitle1"
          >
            {portfolioInvestmentList?.dateStart}{" "}
            {portfolioInvestmentList?.timeStart}
          </Typography>
        </Typography>
      )}

      <Typography variant="h6" fontWeight={500} color="text.main">
        To:{" "}
        <Typography
          component="small"
          color="text.secondary"
          variant="subtitle1"
        >
          {portfolioInvestmentList?.dateEnd} {portfolioInvestmentList?.timeEnd}
        </Typography>
      </Typography>
    </InvestmentBox>
  );
}

const SmallSelect = styled(Select)(({ theme }) => ({
  width: "auto",

  ".MuiSelect-select": {
    padding: theme.spacing(0.5, 1),
    paddingRight: `${theme.spacing(3)} !important`,
    fontSize: "12px",
  },

  ".MuiButtonBase-root": {
    width: "16px",
    height: "16px",
    top: "50%",
    right: "2px",
    transform: "translateY(-50%)",
    padding: 0,
  },
}));

const InvestmentBox = styled(Box)(({ color, theme }) => ({
  background: color,
  borderRadius: "8px",
  padding: theme.spacing(2),
}));

const BoxDatePicker = styled(Box)(() => ({
  marginLeft: "5px",
  ".MuiInputBase-input": {
    padding: "5px",
  },
}));
export default ProfitAndLossBox;
